import ReplyDomain from "@/domains/communityTalk/replyDomain";
import { ReplyGetResponse } from "@/apiRequests/models/communityTalk/replyGetResponse";
import ApiValidationErrorUtil from "@/modules/utils/apiValidationErrorUtil";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { ReviewerAttributeAnswerPostRequest } from "@/apiRequests/models/communityTalk/reviewerAttributeAnswerPostRequest";
import { ReplyPostRequest } from "@/apiRequests/models/communityTalk/replyPostRequest";
import StringUtil from "@/modules/utils/stringUtil";

export default class ReplyController {
	private readonly _replyDomain: ReplyDomain;

	/**
	 * コンストラクタ。
	 */
	constructor() {
		this._replyDomain = new ReplyDomain();
	}

	/**
	 * リプライ取得APIへのリクエスト。
	 * @param replyInitialDisplayCnt リプライ初回取得件数。
	 * @param replyAdditionalDisplayCnt リプライ2回目以降取得件数。
	 * @param targetTalkRoomId 取得対象のトークルームID。
	 * @param isInitialMode 初期表示モードかどうか。
	 * @param tenantId 取得対象のテナントID。
	 * @param subjectCd 議題コード
	 * @param sortKey ソートキー
	 */
	public async getReplyData(
		replyInitialDisplayCnt: number,
		replyAdditionalDisplayCnt: number,
		targetTalkRoomId: string,
		isInitialMode: boolean,
		sortKey: string | null,
		tenantId?: string,
		subjectCd?: string
	): Promise<ReplyGetResponse> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		if (!subjectCd) {
			throw new Error("subjectCd: value not exist.");
		}
		// 初期表示モードの場合、取得件数は初期表示件数とする
		// そうでなければ、取得件数は追加表示件数とする
		const limit = isInitialMode ? replyInitialDisplayCnt : replyAdditionalDisplayCnt;

		try {
			return await this._replyDomain.getReplyData(tenantId, targetTalkRoomId, subjectCd, limit, sortKey);
		} catch (e) {
			throw new Error();
		}
	}

	/**
	 * リプライ投稿APIへのリクエスト。
	 * @param tenantId 取得対象のテナントID。
	 * @param reply 投稿リプライ内容。
	 * @param publishCookieId ReviCoCookieId。
	 * @param siteUserId サイトのユーザーID。
	 */
	public async postReplyData(
		nickName: string,
		reviewerAttributeAnswers: ReviewerAttributeAnswerPostRequest[],
		profilePicIdTmp: string,
		medias: PostMediaViewModel[],
		talkRoomId: string,
		talkRoomUrl?: string,
		tenantId?: string,
		tagCd?: string,
		contents?: string,
		publishCookieId?: string,
		siteUserId?: string
	): Promise<string> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		if (!tagCd) {
			throw new Error("tagCd: value not exist.");
		}
		if (!contents) {
			throw new Error("contents: value not exist.");
		}
		if (!publishCookieId) {
			throw new Error("publishCookieId: value not exist.");
		}

		// APIリクエスト用にデータ成形
		const request: ReplyPostRequest = {
			tenantId: tenantId,
			tagCd: tagCd,
			reply: {
				talkRoomId: talkRoomId,
				nickName: nickName,
				profilePicIdTmp: profilePicIdTmp,
				reviewerAttributeAnswers: reviewerAttributeAnswers,
				contents: contents,
				talkRoomUrl: !StringUtil.isNullOrEmpty(talkRoomUrl) ? (talkRoomUrl as string) : null,
				movies: medias
					.filter((e) => e.type === "Movie")
					.map((e) => ({
						movieId: e.mediaIdTmp,
						thumbnailIndex: e.movieThumbnailIndex as number,
					})),
				images: medias.filter((e) => e.type === "Image").map((e) => ({ imageId: e.mediaIdTmp })),
			},
			publishCookieId: publishCookieId,
			tenantMemberCd: siteUserId,
		};

		try {
			return await this._replyDomain.postReplyData(tenantId, request);
		} catch (e) {
			// ユーザー起因のバリデーションエラーであればバリデーションエラーメッセージとして返却
			if (ApiValidationErrorUtil.isValidateObject_replyPost(e)) {
				const errorResult: ApiErrorViewModel = {
					isValidationError: true,
					validationErrorContents: e as ApiValidationErrorViewModel,
					errorContents: e,
				};
				throw errorResult;
			} else {
				// そうでなければエラーオブジェクトをそのまま返却
				const errorResult: ApiErrorViewModel = {
					isValidationError: false,
					validationErrorContents: null,
					errorContents: e,
				};
				throw errorResult;
			}
		}
	}
}
